import React, { useCallback } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ViewColumnsIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import HeroImageUploader from "./shared/HeroImageUploader";

const PLATFORMS = [
  "instagram",
  "facebook",
  "whatsapp",
  "linkedin",
  "twitter",
  "x",
  "youtube",
  "tiktok",
  "snapchat",
  "website",
];

function Group({ title, subtitle, defaultOpen = true, children, t = (k, f) => f }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {t("footerEditor.common.toggle", "Toggle")}
        </div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const LayoutAlignmentSelector = React.memo(function LayoutAlignmentSelector({ value, onChange, t }) {
  const ALIGN_OPTIONS = [
    {
      value: "left",
      label: t("footerEditor.align.left", "Left"),
      Icon: ArrowLeftIcon,
      description: t("footerEditor.align.leftDesc", "Left side"),
    },
    {
      value: "center",
      label: t("footerEditor.align.center", "Center"),
      Icon: ViewColumnsIcon,
      description: t("footerEditor.align.centerDesc", "Centered"),
    },
    {
      value: "right",
      label: t("footerEditor.align.right", "Right"),
      Icon: ArrowRightIcon,
      description: t("footerEditor.align.rightDesc", "Right side"),
    },
  ];

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <ViewColumnsIcon className="w-4 h-4" />
        {t("footerEditor.layout.alignment", "Content Alignment")}
      </label>

      <div className="grid grid-cols-3 gap-2">
        {ALIGN_OPTIONS.map(({ value: v, label, Icon, description }) => {
          const active = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                active
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div
                className={`mb-2 p-2.5 rounded-lg ${
                  active ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>

              <span className="text-xs font-medium text-gray-700 mb-0.5">{label}</span>
              <span className="text-xs text-gray-500">{description}</span>

              {active ? (
                <div className="absolute top-1.5 right-1.5">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-3 h-3 text-white" />
                  </div>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-gray-500">
        {t(
          "footerEditor.layout.alignmentHint",
          "Controls alignment in the footer (desktop and mobile)."
        )}
      </div>
    </div>
  );
});

export default function FooterSectionEditor({
  config = {},
  general = {},
  pageId,
  onChange,
  uploadImage,
  t = (k, f) => f,
}) {
  const socials = Array.isArray(config.socials) ? config.socials : [];

  const direction = (config.direction || "ltr").toLowerCase();
  const alignment = (
    config.alignment || (direction === "rtl" ? "right" : "left")
  ).toLowerCase();

  const setSocials = (next) => onChange("socials", next);

  const updateSocial = (index, patch) => {
    const next = socials.map((s, i) => (i === index ? { ...(s || {}), ...patch } : s));
    setSocials(next);
  };

  const addSocial = () => {
    setSocials([
      ...(socials || []),
      { platform: "instagram", url: "", label: "", iconUrl: "" },
    ]);
  };

  const removeSocial = (index) => {
    const next = socials.filter((_, i) => i !== index);
    setSocials(next);
  };

  const moveSocial = (from, to) => {
    if (to < 0 || to >= socials.length) return;
    const next = [...socials];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    setSocials(next);
  };

  const uploadAdapter = useCallback(
    async (file, kindString) => {
      if (!uploadImage) throw new Error("Upload disabled");
      if (!pageId) throw new Error("pageId is required");

      const kind = String(kindString || "default");
      return uploadImage(file, { pageId, kind });
    },
    [uploadImage, pageId]
  );

  return (
    <div className="space-y-4">
      <Group
        t={t}
        title={t("footerEditor.group.layout", "Layout")}
        subtitle={t("footerEditor.group.layoutHelp", "Direction and content alignment")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("footerEditor.layout.direction", "Layout Direction")}
            </div>

            <Segmented
              value={direction}
              onChange={(v) => onChange("direction", v)}
              options={[
                { value: "ltr", label: t("footerEditor.common.ltr", "LTR") },
                { value: "rtl", label: t("footerEditor.common.rtl", "RTL") },
              ]}
            />
          </div>

          <LayoutAlignmentSelector
            value={alignment}
            onChange={(v) => onChange("alignment", v)}
            t={t}
          />
        </div>
      </Group>

      <Group
        t={t}
        title={t("footerEditor.group.brand", "Business Info")}
        subtitle={t("footerEditor.group.brandHelp", "Name, description, and logo")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.brand.businessName", "Business Name")}
              </div>
              <input
                value={config.businessName || ""}
                onChange={(e) => onChange("businessName", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.brand.tagline", "Tagline")}
              </div>
              <input
                value={config.businessTagline || ""}
                onChange={(e) => onChange("businessTagline", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("footerEditor.brand.description", "Description")}
            </div>
            <textarea
              value={config.businessDescription || ""}
              onChange={(e) => onChange("businessDescription", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
            />
          </div>
        </div>
      </Group>

      <Group
        t={t}
        title={t("footerEditor.group.contact", "Location and Contact")}
        subtitle={t("footerEditor.group.contactHelp", "Phone, email, and location text")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.location.locationTitle", "Location Title")}
              </div>
              <input
                value={config.locationTitle || ""}
                onChange={(e) => onChange("locationTitle", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("footerEditor.location.locationPlaceholder", "Location")}
              />
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.location.phone", "Phone")}
              </div>
              <input
                value={config.phone || ""}
                onChange={(e) => onChange("phone", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("footerEditor.location.phonePlaceholder", "+972...")}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.location.email", "Email")}
              </div>
              <input
                value={config.email || ""}
                onChange={(e) => onChange("email", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("footerEditor.location.emailPlaceholder", "hello@...")}
              />
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.location.locationText", "Location Text (no map)")}
              </div>
              <input
                value={config.locationText || ""}
                onChange={(e) => onChange("locationText", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t(
                  "footerEditor.location.locationTextPlaceholder",
                  "Street, city, country..."
                )}
              />
            </div>
          </div>
        </div>
      </Group>

      <Group
        t={t}
        title={t("footerEditor.group.socials", "Social Links")}
        subtitle={t("footerEditor.group.socialsHelp", "Manage footer social platforms")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-gray-900">
              {t("footerEditor.socials.title", "Social Links")}
            </div>
            <button
              type="button"
              onClick={addSocial}
              className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              {t("footerEditor.socials.addSocial", "Add Social")}
            </button>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("footerEditor.socials.socialsTitle", "Socials Title")}
            </div>
            <input
              value={config.socialsTitle || ""}
              onChange={(e) => onChange("socialsTitle", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={t("footerEditor.socials.socialsTitlePlaceholder", "Follow us")}
            />
          </div>

          {socials.length === 0 ? (
            <div className="text-sm text-gray-500">
              {t("footerEditor.socials.empty", "No social links yet.")}
            </div>
          ) : (
            <div className="space-y-4">
              {socials.map((s, idx) => (
                <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-gray-700">
                      {t("footerEditor.socials.item", "Social")} #{idx + 1}
                      {(s?.label || s?.platform) ? (
                        <span className="text-gray-400 font-normal">
                          {" "}
                          • {s?.label || s?.platform}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => moveSocial(idx, idx - 1)}
                        disabled={idx === 0}
                        className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t("footerEditor.socials.moveUp", "Up")}
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSocial(idx, idx + 1)}
                        disabled={idx === socials.length - 1}
                        className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t("footerEditor.socials.moveDown", "Down")}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSocial(idx)}
                        className="px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-sm"
                      >
                        {t("footerEditor.common.remove", "Remove")}
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {t("footerEditor.socials.platform", "Platform")}
                      </div>
                      <select
                        value={s?.platform || s?.type || "instagram"}
                        onChange={(e) => updateSocial(idx, { platform: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {PLATFORMS.map((p) => (
                          <option key={p} value={p}>
                            {t(`footerEditor.platforms.${p}`, p)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {t("footerEditor.socials.url", "URL (or WhatsApp phone)")}
                      </div>
                      <input
                        value={s?.url || ""}
                        onChange={(e) => updateSocial(idx, { url: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={t(
                          "footerEditor.socials.urlPlaceholder",
                          "https://... or +972..."
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {t("footerEditor.socials.label", "Label (optional)")}
                      </div>
                      <input
                        value={s?.label || ""}
                        onChange={(e) => updateSocial(idx, { label: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={t(
                          "footerEditor.socials.labelPlaceholder",
                          "Instagram"
                        )}
                      />
                    </div>

                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {t("footerEditor.socials.customIconUrl", "Custom Icon URL (optional)")}
                      </div>
                      <input
                        value={s?.iconUrl || ""}
                        onChange={(e) => updateSocial(idx, { iconUrl: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={t(
                          "footerEditor.socials.customIconPlaceholder",
                          "https://..."
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
                    <div className="shrink-0">
                      {s?.iconUrl ? (
                        <img
                          src={s.iconUrl}
                          alt={t("footerEditor.socials.customIconPreviewAlt", "Custom icon")}
                          className="w-12 h-12 rounded-xl object-contain border bg-white"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-gray-400">
                          {t("footerEditor.socials.defaultIcon", "Default")}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-2">
                        {t(
                          "footerEditor.socials.customIconHint",
                          "Leave empty to use the default platform icon, or upload your own."
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <HeroImageUploader
                          kind="icon"
                          uploadImage={uploadAdapter}
                          onUploaded={(url) => updateSocial(idx, { iconUrl: url })}
                          disabled={!uploadImage || !pageId}
                          t={t}
                        />

                        {s?.iconUrl ? (
                          <button
                            type="button"
                            onClick={() => updateSocial(idx, { iconUrl: "" })}
                            className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                          >
                            {t("footerEditor.common.remove", "Remove")}
                          </button>
                        ) : null}
                      </div>

                      {!pageId ? (
                        <div className="text-xs text-amber-600 mt-2">
                          {t(
                            "footerEditor.socials.saveFirst",
                            "Save the page first before uploading custom icons."
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Group>

      <Group
        t={t}
        title={t("footerEditor.group.privacy", "Privacy Policy")}
        subtitle={t("footerEditor.group.privacyHelp", "Footer link and privacy page content")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.privacy.footerLinkText", "Footer Link Text")}
              </div>
              <input
                value={config.privacyText || config.privacyLinkText || ""}
                onChange={(e) => onChange("privacyText", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t(
                  "footerEditor.privacy.footerLinkTextPlaceholder",
                  "Privacy Policy"
                )}
              />
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("footerEditor.privacy.pageTitle", "Privacy Page Title")}
            </div>
            <input
              value={config.privacyTitle || ""}
              onChange={(e) => onChange("privacyTitle", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("footerEditor.privacy.content", "Privacy Policy Content")}
            </div>
            <textarea
              value={config.privacyContent || ""}
              onChange={(e) => onChange("privacyContent", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              rows={6}
            />
          </div>
        </div>
      </Group>

      <Group
        t={t}
        title={t("footerEditor.group.developer", "Developer Credit")}
        subtitle={t("footerEditor.group.developerHelp", "Developer info, logo, and copyright")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.developer.developedByText", "Developed By Text")}
              </div>
              <input
                value={config.developedByText || ""}
                onChange={(e) => onChange("developedByText", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t(
                  "footerEditor.developer.developedByPlaceholder",
                  "Developed by"
                )}
              />
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.developer.developerName", "Developer Name")}
              </div>
              <input
                value={config.developerName || ""}
                onChange={(e) => onChange("developerName", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.developer.developerLink", "Developer Link")}
              </div>
              <input
                value={config.developerLink || config.developerWebsite || ""}
                onChange={(e) => onChange("developerLink", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("footerEditor.common.urlPlaceholder", "https://...")}
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700 mb-1">
                {t("footerEditor.developer.developerLogoUrl", "Developer Logo")}
              </div>

              <input
                value={config.developerLogo || ""}
                onChange={(e) => onChange("developerLogo", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("footerEditor.common.urlPlaceholder", "https://...")}
              />

              <HeroImageUploader
                kind="logo"
                uploadImage={uploadAdapter}
                onUploaded={(url) => onChange("developerLogo", url)}
                disabled={!uploadImage || !pageId}
                t={t}
              />

              {!pageId ? (
                <div className="text-xs text-amber-600">
                  {t(
                    "footerEditor.developer.saveFirst",
                    "Save the page first before uploading a developer logo."
                  )}
                </div>
              ) : null}

              {config.developerLogo ? (
                <div className="pt-2 flex items-center gap-3">
                  <img
                    src={config.developerLogo}
                    alt={t("footerEditor.developer.developerLogoAlt", "Developer")}
                    className="w-14 h-14 rounded-2xl object-cover border"
                  />
                  <button
                    type="button"
                    onClick={() => onChange("developerLogo", "")}
                    className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                  >
                    {t("footerEditor.common.remove", "Remove")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("footerEditor.developer.copyrightText", "Copyright Text")}
            </div>
            <input
              value={config.copyrightText || ""}
              onChange={(e) => onChange("copyrightText", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={t(
                "footerEditor.developer.copyrightPlaceholder",
                "All rights reserved."
              )}
            />
          </div>
        </div>
      </Group>
    </div>
  );
}